import { UserAccountType } from "@common/constants";
import type { UserAccount } from "@data-types";
import type { RootStore } from "@state/stores/types";
import { AccountTypes } from "@utils/common/activeAccountEnum";

export function getDefaultOnlineWallet(userAccounts: UserAccount[] | null) {
	return userAccounts?.find(
		(account) =>
			account.abrv == UserAccountType.SportBettingAccountOnline &&
			account.isPrimary
	);
}

export function getWallet(
	shopId: string | undefined,
	abrv: string | undefined,
	userAccounts?: UserAccount[]
): UserAccount | undefined {
	if (!shopId || !abrv) return undefined;
	return userAccounts?.find((w) => w.shopId === shopId && w.abrv === abrv);
}

/**
 * Maps betting account type abbreviation to account type IDs
 */
export function mapBettingTypeAbrvToId(
	params: BettingTypeIdParams
): string[] | null {
	const { bettingAccountTypeAbrv, shopId, rootStore } = params;

	if (bettingAccountTypeAbrv == null) {
		return null;
	}

	const bettingTypeIds: string[] = [];
	const userAccounts = rootStore.userAuthStore.userAccounts;

	if (!userAccounts?.length) {
		return null;
	}

	if (
		bettingAccountTypeAbrv === UserAccountType.SportBettingAccountShopOnline
	) {
		const matchingWallets = userAccounts.filter(
			(w) =>
				w.shopId === shopId &&
				(w.abrv === UserAccountType.SportBettingAccountShopOnline ||
					w.abrv === UserAccountType.CashToDigitalAccount)
		);

		matchingWallets.forEach((wallet) => {
			bettingTypeIds.push(wallet.accountTypeId);
		});
	} else {
		const wallet = userAccounts.find(
			(w) => w.shopId === shopId && w.abrv === bettingAccountTypeAbrv
		);

		if (wallet) {
			bettingTypeIds.push(wallet.accountTypeId);
		}
	}

	return bettingTypeIds.length > 0 ? bettingTypeIds : null;
}

/**
 * Determines the appropriate shop ID and betting account type abbreviation
 * my bets specific function
 */
export function getShopIdAndBettingTypeAbrv(
	params: ShopIdAndBettingTypeParams
): ShopIdAndBettingTypeResult {
	const {
		rawDataBettingAccountTypeAbrv,
		rawDataShopId,
		onlineAccountTypeAbrv,
		onlineShopAccountTypeAbrv,
		rootStore,
	} = params;

	let bettingAccountTypeAbrv: string | null =
		rawDataBettingAccountTypeAbrv ||
		onlineAccountTypeAbrv ||
		onlineShopAccountTypeAbrv ||
		null;

	let shopId: string | null =
		rootStore.userAuthStore.defaultOnlineShopId ||
		rootStore.userAuthStore.activeShopUserShopId ||
		null;

	if (bettingAccountTypeAbrv === UserAccountType.SportBettingAccountOnline) {
		shopId =
			rawDataShopId ||
			rootStore.userAuthStore.defaultOnlineShopId ||
			null;
	} else {
		if (rawDataShopId) {
			const doesExists = rootStore.userAuthStore.shopUserWallets.find(
				(shop) => shop.shopId === rawDataShopId
			);

			if (!doesExists) {
				if (rootStore.userAuthStore.defaultOnlineShopId) {
					bettingAccountTypeAbrv =
						UserAccountType.SportBettingAccountOnline;
					shopId = rootStore.userAuthStore.defaultOnlineShopId;
				} else {
					bettingAccountTypeAbrv =
						UserAccountType.SportBettingAccountShopOnline;
					shopId =
						rootStore.userAuthStore.activeShopUserShopId || null;
				}
			} else {
				shopId = rawDataShopId;
			}
		} else {
			shopId = rootStore.userAuthStore.activeShopUserShopId || null;
		}
	}

	return { shopId, bettingAccountTypeAbrv };
}

export function getDefaultUserWalletByAccountType(
	activeAccount?: AccountTypes
): UserAccount | undefined {
	const userAuthStore = App.state.rootStore.userAuthStore;

	const user =
		activeAccount === AccountTypes.ONLINE
			? userAuthStore.onlineUser
			: userAuthStore.shopUser;

	if (!userAuthStore.userAccounts?.length) {
		return undefined;
	}

	return (
		userAuthStore.userAccounts.find((entry) => {
			if (entry.userId !== user?.id) return false;

			if (activeAccount === AccountTypes.ONLINE) {
				return (
					entry.abrv === UserAccountType.SportBettingAccountOnline &&
					entry.isPrimary
				);
			} else {
				return (
					entry.abrv ===
						UserAccountType.SportBettingAccountShopOnline &&
					entry.isCurrentlyActive
				);
			}
		}) ?? userAuthStore.userAccounts[0]
	);
}
export interface BettingTypeIdParams {
	bettingAccountTypeAbrv?: string | null;
	shopId?: string | null;
	rootStore: RootStore;
}

export interface ShopIdAndBettingTypeParams {
	rawDataBettingAccountTypeAbrv?: string | null;
	rawDataShopId?: string | null;
	onlineAccountTypeAbrv?: string | null;
	onlineShopAccountTypeAbrv?: string | null;
	rootStore: RootStore;
}

export interface ShopIdAndBettingTypeResult {
	shopId: string | null;
	bettingAccountTypeAbrv: string | null;
}

/**
 * Builds betting account type string, combining shop online with cash-to-digital if available
 */
export function buildBettingAccountTypeString(
	params: BuildBettingAccountTypeParams
): string {
	const { bettingAccountTypeAbrv, shopId, rootStore } = params;

	if (
		bettingAccountTypeAbrv ===
			UserAccountType.SportBettingAccountShopOnline &&
		shopId
	) {
		const shop = rootStore.userAuthStore.shopUserShops.get(shopId);
		const cashToDigitalWallet = shop?.wallets.find(
			(w) => w.abrv === UserAccountType.CashToDigitalAccount
		);

		if (cashToDigitalWallet) {
			return `${UserAccountType.SportBettingAccountShopOnline},${UserAccountType.CashToDigitalAccount}`;
		}
	}

	return bettingAccountTypeAbrv;
}

export function getWalletByShopIdAndAccountTypeId(
	shopId: string | undefined,
	bettingAccountTypeId: string | undefined,
	userAccounts?: UserAccount[]
): UserAccount | undefined {
	if (!shopId || !bettingAccountTypeId) return undefined;
	return userAccounts?.find(
		(w) => w.shopId === shopId && w.accountTypeId === bettingAccountTypeId
	);
}

export interface BuildBettingAccountTypeParams {
	bettingAccountTypeAbrv: string;
	shopId?: string | null;
	rootStore: RootStore;
}
